import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ClipboardList, Check } from 'lucide-react';

const SERVICES_BY_MODULE = {
  ch_company: [
    { key: 'svc_accounts_prep', label: 'Accounts Preparation' },
    { key: 'svc_bookkeeping', label: 'Bookkeeping' },
    { key: 'svc_ct600', label: 'CT600 Return' },
    { key: 'svc_confirmation_statement', label: 'Confirmation Statement' },
    { key: 'svc_payroll', label: 'Payroll' },
    { key: 'svc_pension_ae', label: 'Pension Auto-Enrolment' },
    { key: 'svc_vat_returns', label: 'VAT Returns' },
    { key: 'svc_cfo', label: 'CFO' },
    { key: 'svc_mgmt_accounts', label: 'Management Accounts' },
    { key: 'svc_cis', label: 'CIS' },
    { key: 'svc_registered_address', label: 'Registered Address' },
    { key: 'svc_accounting_software', label: 'Accounting Software' },
    { key: 'svc_consultation', label: 'Consultation / Advice' },
  ],
  charity: [
    { key: 'svc_accounts_prep', label: 'Accounts Preparation' },
    { key: 'svc_bookkeeping', label: 'Bookkeeping' },
    { key: 'svc_charity_commission', label: 'Charity Commission Return' },
    { key: 'svc_independent_exam', label: 'Independent Examination' },
    { key: 'svc_payroll', label: 'Payroll' },
    { key: 'svc_pension_ae', label: 'Pension Auto-Enrolment' },
    { key: 'svc_gift_aid', label: 'Gift Aid' },
    { key: 'svc_accounting_software', label: 'Accounting Software' },
    { key: 'svc_consultation', label: 'Consultation / Advice' },
  ],
  individual: [
    { key: 'svc_self_assessment', label: 'Self Assessment Tax Return' },
    { key: 'svc_mtd_itsa', label: 'MTD ITSA' },
    { key: 'svc_cis', label: 'CIS' },
    { key: 'svc_bookkeeping', label: 'Bookkeeping' },
    { key: 'svc_vat_returns', label: 'VAT Returns' },
    { key: 'svc_payroll', label: 'Payroll' },
    { key: 'svc_pension_ae', label: 'Pension Auto-Enrolment' },
    { key: 'svc_accounting_software', label: 'Accounting Software' },
    { key: 'svc_consultation', label: 'Consultation / Advice' },
  ],
  unregistered: [
    { key: 'svc_accounts_prep', label: 'Accounts Preparation' },
    { key: 'svc_bookkeeping', label: 'Bookkeeping' },
    { key: 'svc_independent_exam', label: 'Independent Examination' },
    { key: 'svc_payroll', label: 'Payroll' },
    { key: 'svc_pension_ae', label: 'Pension Auto-Enrolment' },
    { key: 'svc_vat_returns', label: 'VAT Returns' },
    { key: 'svc_cfo', label: 'CFO' },
    { key: 'svc_mgmt_accounts', label: 'Management Accounts' },
    { key: 'svc_cis', label: 'CIS' },
    { key: 'svc_gift_aid', label: 'Gift Aid' },
    { key: 'svc_accounting_software', label: 'Accounting Software' },
    { key: 'svc_consultation', label: 'Consultation / Advice' },
  ],
  partnership: [
    { key: 'svc_accounts_prep', label: 'Accounts Preparation' },
    { key: 'svc_bookkeeping', label: 'Bookkeeping' },
    { key: 'svc_payroll', label: 'Payroll' },
    { key: 'svc_pension_ae', label: 'Pension Auto-Enrolment' },
    { key: 'svc_vat_returns', label: 'VAT Returns' },
    { key: 'svc_cfo', label: 'CFO' },
    { key: 'svc_mgmt_accounts', label: 'Management Accounts' },
    { key: 'svc_cis', label: 'CIS' },
    { key: 'svc_registered_address', label: 'Registered Address' },
    { key: 'svc_accounting_software', label: 'Accounting Software' },
    { key: 'svc_consultation', label: 'Consultation / Advice' },
  ],
};

const BulkServicesModal = ({ selectedClients, config, onUpdateServices, onClose }) => {
  const services = useMemo(() => SERVICES_BY_MODULE[config?.id] || [], [config?.id]);
  const [serviceModes, setServiceModes] = useState(() =>
    Object.fromEntries(services.map(svc => [svc.key, 'ignore']))
  );

  const updateMode = (key, value) => {
    setServiceModes(prev => ({ ...prev, [key]: value }));
  };

  const selectedChangeCount = useMemo(
    () => Object.values(serviceModes).filter(mode => mode !== 'ignore').length,
    [serviceModes]
  );

  const handleApply = () => {
    const updates = {};
    Object.entries(serviceModes).forEach(([key, mode]) => {
      if (mode === 'engage') updates[key] = true;
      if (mode === 'disengage') updates[key] = false;
    });

    if (Object.keys(updates).length > 0) {
      onUpdateServices(updates);
      onClose();
    }
  };

  if (services.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-violet-600" />
            <h3 className="text-lg font-semibold text-slate-800">Bulk Edit Engaged Services</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(85vh-146px)]">
          <p className="text-sm text-slate-600">
            Apply service engagement updates to {selectedClients.length} selected client{selectedClients.length !== 1 ? 's' : ''}.
          </p>

          <div className="space-y-2">
            {services.map((svc) => (
              <div key={svc.key} className="grid grid-cols-[minmax(0,1fr)_150px] items-center gap-3 rounded-md border border-slate-200 px-3 py-2">
                <div className="text-sm text-slate-700 font-medium">{svc.label}</div>
                <select
                  value={serviceModes[svc.key] || 'ignore'}
                  onChange={(e) => updateMode(svc.key, e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-xs bg-white text-slate-800 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  style={{ colorScheme: 'light' }}
                >
                  <option value="ignore" className="bg-white text-slate-800">No change</option>
                  <option value="engage" className="bg-white text-slate-800">Set engaged</option>
                  <option value="disengage" className="bg-white text-slate-800">Set not engaged</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={selectedChangeCount === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Apply Changes
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkServicesModal;
